import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../api/authApi'
import type { User, UserRole } from '@/features/users/types/user'
import { getContext } from '@/integrations/tanstack-query/root-provider'
import { PWA_TOKEN_CONFIG, isPWA } from '@/integrations/pwaTokenConfig'

const { queryClient } = getContext()

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  expiresAt: string | null
  activeRole: UserRole | null
  isAuthenticated: boolean
  _navigate: ((to: string) => void) | null

  // Actions
  login: (
    user: User,
    accessToken: string,
    refreshToken: string,
    expiresAt: string,
  ) => void
  logout: () => void
  setUser: (user: User) => void
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresAt: string,
    user?: User, // Optional user param to update expireAt
  ) => void
  setActiveRole: (role: UserRole) => void
  setNavigation: (navigate: (to: string) => void) => void
}

function isTokenValid(user: User | null): boolean {
  if (!user) return false
  const expires = user.expireAt
  const expDate = typeof expires === 'string' ? new Date(expires) : expires

  // For PWA users, extend token validity period
  if (isPWA() && PWA_TOKEN_CONFIG.USE_EXTENDED_STORAGE) {
    const extendedExpiry = new Date(
      Date.now() + PWA_TOKEN_CONFIG.REFRESH_TOKEN_EXTENDED_EXPIRY,
    )
    return expDate
      ? expDate.getTime() > Date.now() && expDate < extendedExpiry
      : false
  }

  return expDate ? expDate.getTime() > Date.now() : false
}

function getInitialAuthState() {
  try {
    const raw = localStorage.getItem('auth-storage')
    if (raw) {
      const parsed = JSON.parse(raw)
      const state = parsed.state || parsed

      // Check if token is expired
      if (state.user?.expireAt) {
        const expiryDate = new Date(state.user.expireAt)
        if (expiryDate.getTime() < Date.now()) {
          console.log('Stored token expired, clearing...')
          localStorage.removeItem('auth-storage')
          return getDefaultAuthState()
        }
      }

      if (isTokenValid(state.user)) {
        const activeRole = state.activeRole || (state.user?.roles?.[0] ?? null)
        return { ...state, activeRole }
      }
    }
  } catch (error) {
    console.log('Failed to parse auth state:', error)
  }
  return getDefaultAuthState()
}

function getDefaultAuthState() {
  return {
    user: null,
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    isAuthenticated: false,
    activeRole: null,
    _navigate: null,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const initialState = getInitialAuthState()

      return {
        ...initialState,
        _navigate: null,
        setNavigation: (navigate) => {
          set({ _navigate: navigate })
        },

        login: (user, accessToken, refreshToken, expiresAt) => {
          // For PWA users, extend refresh token expiry
          const finalExpiresAt =
            isPWA() && PWA_TOKEN_CONFIG.USE_EXTENDED_STORAGE
              ? new Date(
                  Date.now() + PWA_TOKEN_CONFIG.REFRESH_TOKEN_EXTENDED_EXPIRY,
                ).toISOString()
              : expiresAt

          const activeRole = user.roles.length === 1 ? user.roles[0] : null

          set({
            user,
            accessToken,
            refreshToken,
            expiresAt: finalExpiresAt,
            isAuthenticated: true,
            activeRole,
          })
        },

        logout: async () => {
          const { refreshToken, _navigate } = get()

          console.log('Logout initiated...')

          // Call logout API first (before clearing state)
          if (refreshToken) {
            try {
              await authApi.logout()
            } catch (error) {
              console.error('Logout API call failed:', error)
              // Continue with local logout even if API fails
            }
          }

          // Clear auth state first so AuthedLayoutWithUsers returns null
          // before queryClient.clear() triggers re-renders with isLoading=true
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            isAuthenticated: false,
            activeRole: null,
          })

          // Clear localStorage
          localStorage.removeItem('auth-storage')

          // Clear query cache after auth state is cleared
          queryClient.clear()

          console.log('Auth state cleared, navigating to login...')

          // Navigate to login using the stored navigation function
          if (_navigate) {
            _navigate('/login')
          } else {
            // Fallback: force page reload to login
            window.location.href = '/login'
          }
        },

        setUser: (user) => {
          set({ user })
        },

        setTokens: (accessToken, refreshToken, expiresAt, user) => {
          // For PWA users, extend refresh token expiry
          const finalExpiresAt =
            isPWA() && PWA_TOKEN_CONFIG.USE_EXTENDED_STORAGE
              ? new Date(
                  Date.now() + PWA_TOKEN_CONFIG.REFRESH_TOKEN_EXTENDED_EXPIRY,
                ).toISOString()
              : expiresAt

          // Only update user if provided (to update expireAt)
          // This prevents unnecessary re-renders
          if (user) {
            set({
              accessToken,
              refreshToken,
              expiresAt: finalExpiresAt,
              user: {
                ...get().user!,
                expireAt: user.expireAt,
              },
            })
          } else {
            set({ accessToken, refreshToken, expiresAt: finalExpiresAt })
          }
        },

        setActiveRole: (role) => {
          const { user } = get()
          if (user && user.roles.includes(role)) {
            set({ activeRole: role })
          }
        },
      }
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        activeRole: state.activeRole,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
